/* eslint-disable */
import { connect } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { Trash2, RefreshCcw, Pencil } from 'lucide-react';
import { formActions, modalActions } from '@/Actions';
import { notificationServices } from '@/Services';
import wastageAnalyticsServices from '@/Services/Analytics Services/wastage-analytics.services';
import { AddWastageReasonModal } from '@/Components/Modals/AddWastageReasonModal';
import { AlertModal } from '@/Components/Modals/AlertModal';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useAuth } from '@/Components/Auth/Login/auth';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';

const COL_MAP = {
	id: 'id',
	storeType: 'storeType',
	storeSubType: 'storeSubType',
	categoryName: 'productCategoryName',
	skuType: 'skuType',
	reason: 'reason',
	actions: 'id',
};

const WastageReasons = (props) => {
	const {
		toggleAddWastageReasonModal,
		productCategoriesList,
		fetchProductCategory,
		toggleAlert,
	} = props;
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};

	const [wastageReasonsList, setWastageReasonsList] = useState([]);
	const [fetchLoading, setFetchLoading] = useState(false);

	// Fetch the master category list once if it isn't already in Redux —
	// we use it to render the Category Name cell from the row's categoryId.
	useEffect(() => {
		if (Array.isArray(productCategoriesList) && productCategoriesList.length > 0) return;
		fetchProductCategory({
			entity: 'PRODUCT_CATEGORY',
			req: {
				params: { id: '' },
			},
			method: 'LIST',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fast id → categoryName lookup so the row renderer doesn't iterate the
	// list per cell.
	const categoryNameById = useMemo(() => {
		const map = new Map();
		(productCategoriesList ?? []).forEach((cat) => {
			if (cat?.id != null) {
				map.set(
					cat.id,
					cat?.productCategoryName ?? cat?.categoryName ?? cat?.name ?? '-'
				);
			}
		});
		return map;
	}, [productCategoriesList]);

	const handleAddClick = () => {
		toggleAddWastageReasonModal(true, {
			onSuccessCallback: () => {
				handleListFetch();
			},
		});
	};

	// Open the same modal in edit mode, pre-filled with the row's values.
	// The modal reads `data.isEdit` + `data.record` to switch into edit mode
	// (different heading, "Update" button, and id sent in the API payload).
	const handleEdit = (row) => {
		toggleAddWastageReasonModal(true, {
			isEdit: true,
			heading: 'Edit Wastage Reason',
			record: row,
			onSuccessCallback: () => {
				handleListFetch();
			},
		});
	};

	const handleListFetch = async () => {
		setFetchLoading(true);
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Fetching Wastage Reasons...',
		});
		try {
			const response =
				await wastageAnalyticsServices.fetchWastageReasonsList({
					req: {
						params: {},
					},
				});
			if (response?.statusCode === 200) {
				setWastageReasonsList(response?.data);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
		} finally {
			notificationServices.closeNotification(loadingId);
			setFetchLoading(false);
		}
	};

	// Actual deletion — fired by AlertModal's confirm action.
	const confirmDelete = async (reason) => {
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Deleting Wastage Reason...',
		});
		try {
			const response = await wastageAnalyticsServices.deleteWastageReason(
				{
					req: {
						params: {
							reasonId: reason?.id,
						},
					},
				}
			);
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message || 'Wastage Reason deleted successfully',
				});
				handleListFetch();
			} else {
				throw new Error(response?.message || 'Failed to delete wastage reason');
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || 'Failed to delete wastage reason',
			});
			console.log('error', error);
		} finally {
			notificationServices.closeNotification(loadingId);
			toggleAlert(false);
		}
	};

	// Click handler from the row — opens AlertModal confirmation. Delete
	// only happens if the user confirms.
	const handleDelete = (reason) => {
		toggleAlert(true, {
			heading: 'Delete Wastage Reason?',
			message: `Are you sure you want to delete "${reason?.reason ?? ''}"? This action cannot be undone.`,
			confirmText: 'Delete',
			cancelText: 'Cancel',
			colorReverse: false,
			handleConfirm: () => {
				confirmDelete(reason);
			},
		});
	};

	useEffect(() => {
		handleListFetch();
	}, []);

	// Decorate every row with the resolved productCategoryName so it is
	// available to the cell renderer.
	const tableData = useMemo(() => {
		return (wastageReasonsList ?? []).map((row) => {
			const cid = row?.categoryId
				?? row?.productCategoryId
				?? row?.category?.id;
			return {
				...row,
				productCategoryName: row?.productCategoryName
					?? (cid != null ? categoryNameById.get(cid) : null)
					?? '-',
			};
		});
	}, [wastageReasonsList, categoryNameById]);

	// Sorting removed — every column opts into customCol so the header is
	// rendered left-aligned via the table-level customCol callback.
	const columns = useMemo(() => {
		const base = [
			{ key: 'id', title: 'ID', width: 70, customCol: true },
			{ key: 'storeType', title: 'Store Type', width: 150, customCol: true },
			{ key: 'storeSubType', title: 'Store Sub Type', width: 170, customCol: true },
			{ key: 'categoryName', title: 'Category Name', width: 200, customCol: true },
			{ key: 'skuType', title: 'SKU Type', width: 140, customCol: true },
			{ key: 'reason', title: 'Wastage Reason', width: 200, customCol: true },
		];
		if (level !== 1) {
			base.push({ key: 'actions', title: 'Actions', width: 110, customCol: true });
		}
		return base;
	}, [level]);

	// Header renderer — applied to every column. Left-aligned label.
	const renderColHeader = (column) => (
		<div className='w-full flex items-center justify-start text-left'>
			{column?.title}
		</div>
	);

	// Body cells — left-aligned, with the Delete column rendered as an
	// icon-only IconButton wrapped in an MUI Tooltip.
	const customRowData = useMemo(() => ({
		id: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words'>
				{row?.id ?? '-'}
			</div>
		),
		storeType: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words'>
				{row?.storeType ?? '-'}
			</div>
		),
		storeSubType: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words'>
				{row?.storeSubType ?? '-'}
			</div>
		),
		categoryName: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words'>
				{row?.productCategoryName ?? '-'}
			</div>
		),
		skuType: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words'>
				{row?.skuType ?? '-'}
			</div>
		),
		reason: (row) => (
			<div className='text-left text-[13px] text-gray-800 whitespace-normal break-words leading-snug'>
				{row?.reason ?? '-'}
			</div>
		),
		actions: (row) => (
			<div className='text-left flex items-center gap-1'>
				<Tooltip title='Edit' placement='top' arrow>
					<IconButton
						size='small'
						onClick={() => handleEdit(row)}
						sx={{ color: '#2563eb' }}
					>
						<Pencil size={16} />
					</IconButton>
				</Tooltip>
				<Tooltip title='Delete' placement='top' arrow>
					<IconButton
						size='small'
						onClick={() => handleDelete(row)}
						sx={{ color: '#dc2626' }}
					>
						<Trash2 size={16} />
					</IconButton>
				</Tooltip>
			</div>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [wastageReasonsList]);

	return (
		<div>
			<AddWastageReasonModal />
			<AlertModal />
			<div className='p-2'>
				<div className='flex justify-between m-4 '>
					<span className='font-bold text-[16px]'>Wastage Reasons</span>
					<div className='flex items-center gap-2'>
						<Tooltip title='Fetch Wastage Reason List' placement='top' arrow>
							<IconButton
								onClick={handleListFetch}
								size='small'
								className='bg-gray-100 hover:bg-gray-200'
								disabled={fetchLoading}
							>
								<RefreshCcw size={16} className={fetchLoading ? 'animate-spin' : ''} />
							</IconButton>
						</Tooltip>
						{level !== 1 && (
							<Button
								onClick={handleAddClick}
								className='bg-yellow-400 hover:bg-yellow-500 rounded-md flex items-center justify-center gap-2 text-black'
							>
								+ Add Reason
							</Button>
						)}
					</div>
				</div>
			</div>
			<div className='px-4 pb-4 h-[calc(100vh-180px)]'>
				<VariantTable
					data={tableData}
					columns={columns}
					colMapping={COL_MAP}
					customRowData={customRowData}
					customCol={renderColHeader}
					virtualization
					customInfoLine='No wastage reasons found'
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const productCategoriesList = state?.form?.PRODUCT_CATEGORY?.data?.LIST ?? [];
	return {
		productCategoriesList,
	};
};

const mapDispatchToProps = {
	toggleAddWastageReasonModal: modalActions.toggleAddWastageReasonModal,
	fetchProductCategory: formActions.gateway,
	toggleAlert: modalActions.toggleAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(WastageReasons);
